// DAS-3044: one cost line for an advice run, shown wherever the run is opened
// (the LLM Portfolio Analysis header and the "full analysis" view of the LLM
// Advice tab). The stored totals come from the pipeline's cost line: since
// DAS-3035 the Claude part is the command-line tool's own cache-aware figure,
// DeepSeek/Gemini stay priced from tokens. Nothing here is invoiced: the Claude
// calls run on the subscription; the tokens show the allowance a run used.
import React from 'react';

function fmtTokens(n) {
  const v = Number(n || 0);
  if (v >= 1000) return `${Math.round(v / 1000)}k`;
  return String(v);
}

// DAS-2522: claude = total − deepseek − gemini (three providers, exact totals);
// only providers that actually incurred cost are named.
export function costParts(cost) {
  if (!cost || cost.total == null) return null;
  const ds = Number(cost.deepseek || 0);
  const gem = Number(cost.gemini || 0);
  const claude = Math.max(0, Number(cost.total) - ds - gem);
  const parts = [];
  if (claude > 5e-6) parts.push({ name: 'Claude', usd: claude });
  if (ds > 5e-6) parts.push({ name: 'DeepSeek', usd: ds });
  if (gem > 5e-6) parts.push({ name: 'Gemini', usd: gem });
  return parts;
}

export function costFromResponse(data) {
  if (!data) return null;
  return {
    total: data.total_cost_usd,
    deepseek: data.deepseek_cost_usd,
    gemini: data.gemini_cost_usd,
    inTok: data.input_tokens,
    outTok: data.output_tokens,
    calls: data.llm_api_calls,
    providers: data.providers_used || null,   // DAS-3367
  };
}

// DAS-3367: the models that ANSWERED, from the stored call counts. The cost split cannot say it:
// Claude on the subscription costs $0, so a cost-based list left Claude out on a morning it wrote
// every step. Old rows without providers_used fall back to the cost-based list.
const PROVIDER_NAME = { claude: 'Claude', deepseek: 'DeepSeek', gemini: 'Gemini', openai: 'OpenAI' };
const providerName = (p) => PROVIDER_NAME[p] || p;
export function answeredLabel(providers) {
  const answered = (providers && providers.answered) || {};
  const names = Object.keys(answered).filter((p) => Number(answered[p]) > 0);
  if (!names.length) return null;
  names.sort((a, b) => Number(answered[b]) - Number(answered[a]));
  return names.map((p) => `${providerName(p)} (${answered[p]} call${Number(answered[p]) === 1 ? '' : 's'})`).join(' + ');
}
export function failedLabel(providers) {
  const failed = (providers && providers.failed) || {};
  const names = Object.keys(failed).filter((p) => Number(failed[p]) > 0);
  if (!names.length) return null;
  return names.map((p) => `${providerName(p)} failed ${failed[p]}×`).join(', ') + ': another model answered instead';
}

export default function LLMCostLine({ cost, timestamp = null, style = {} }) {
  const parts = costParts(cost);
  const answered = answeredLabel(cost && cost.providers);
  const failed = failedLabel(cost && cost.providers);
  const modelsLabel = answered || (parts && parts.length ? parts.map((p) => p.name).join(' + ') : 'Claude + DeepSeek');
  return (
    <div
      data-testid="llm-cost-line"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: '12.5px', color: 'rgba(0,0,0,0.6)', ...style }}
    >
      {timestamp && <span>🕒 {timestamp}</span>}
      <span data-testid="llm-models-answered">· Models: {modelsLabel}</span>
      {failed && (
        <span data-testid="llm-models-failed" style={{ color: '#cf1322', fontWeight: 600 }}>⚠ {failed}</span>
      )}
      {cost && cost.total != null && (
        <span>
          {' '}· 💰 <strong>${Number(cost.total).toFixed(5)}</strong>
          {parts && parts.length > 0 && <> ({parts.map((p) => `${p.name} $${p.usd.toFixed(5)}`).join(' · ')})</>}
          {cost.calls != null && <> · {cost.calls} calls</>}
          {(cost.inTok != null || cost.outTok != null) && (
            <> · {fmtTokens(cost.inTok)} tokens in · {fmtTokens(cost.outTok)} out</>
          )}
        </span>
      )}
    </div>
  );
}
